import React, { useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, FormControlLabel, Grid, IconButton } from '@mui/material';
import { BusinessOutlined, CloudDownloadOutlined, DeleteOutlined, DownloadOutlined, EditOutlined } from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';
import { addOrUpdateTechnicalSession, deleteTechnicalSession, getAllInstitutions, getAllTechnicalSessions, getAllTechnicalSessionsDT } from '../../../redux/api/institution';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import TextInput from '../../../components/common/TextInput';
import SelectInput from '../../../components/common/SelectInput';
import DocumentInput from '../../../components/common/DocumentInput';
import { getDocument } from '../../../redux/api/workflows';
import { technicalSessionColumns } from '../../../util';
import Filters from '../../../components/Filters';

const TechnicalSessions = () => {
    const [processing, setProcessing] = useState(false);
    const [performingAction, setPerformingAction] = useState(false);
    const defaultSession = {
        id: '',
        title: '',
        institutionId: '',
        documentId: '',
        attachmentDocument: '',
        description: '',
        institutionName: '',
        documentFileName: '',
        documentFileSize: '',
        startDate: '',
        endDate: '',
    };
    const [actionModal, setActionModal] = useState(false);
    const [deleteModal, showDeleteModal] = useState(false);
    const [selectedSession, setSelectedSession] = useState(defaultSession); // activate || deactivate
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [records, setRecords] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [existingSearch, setExistingSearch] = useState({});

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 80,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.id) || 0) + 1}</p>;
            },
        },
        {
            field: 'action',
            headerName: 'Quick Action',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <IconButton
                            size="small"
                            color="primary"
                            variant="outlined"
                            title="Edit"
                            onClick={() => {
                                setSelectedSession({
                                    ...params?.row,
                                    startDate: params?.row?.startDate ? moment(params?.row?.startDate).format('YYYY-MM-DD') : '',
                                    endDate: params?.row?.endDate ? moment(params?.row?.endDate).format('YYYY-MM-DD') : '',
                                });
                                setActionModal(true);
                            }}
                        >
                            <EditOutlined />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="info"
                            variant="outlined"
                            title="Download"
                            onClick={async () => {
                                await getDocument(params?.row?.documentId, params?.row?.documentFileName);
                            }}
                        >
                            <DownloadOutlined />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="error"
                            variant="outlined"
                            title="Edit"
                            onClick={() => {
                                setSelectedSession(params?.row);
                                showDeleteModal(true);
                            }}
                        >
                            <DeleteOutlined />
                        </IconButton>
                    </div>
                );
            },
        },
        { field: 'title', headerName: 'Title', width: 200 },
        { field: 'description', headerName: 'Description', width: 300 },

        { field: 'institutionName', headerName: 'Institution', width: 350 },
        {
            field: 'startDate',
            headerName: 'Start Date',
            width: 350,
            valueGetter: (params) => (params?.row?.startDate ? moment(params?.row?.startDate).format('DD/MM/YY hh:mm') : ''),
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            width: 350,
            valueGetter: (params) => (params?.row?.endDate ? moment(params?.row?.endDate).format('DD/MM/YY hh:mm') : ''),
        },
        {
            field: 'createdOn',
            headerName: 'Creation Date',
            width: 200,
            valueGetter: (params) => (params?.row?.createdOn ? moment(params?.row?.createdOn).format('DD-MM-YYYY hh:mm A') : ''),
        },
        // { field: 'createdBy', headerName: 'Creation By', width: 200 }
    ];

    const performAction = async (values) => {
        try {
            setPerformingAction(true);
            const formData = new FormData();
            Object.keys(values).forEach((key) => {
                formData.append(key, values[key]);
            });
            await addOrUpdateTechnicalSession(formData);
            enqueueSnackbar(`Technical Session ${values?.id ? 'edited' : 'added'} successfully`);
            init();
            setActionModal(null);
            setSelectedSession(defaultSession);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setPerformingAction(false);
        }
    };

    const deleteSessionData = async (values) => {
        try {
            setPerformingAction(true);
            await deleteTechnicalSession(values?.id);
            enqueueSnackbar(`Technical Session Deleted successfully`);
            init();
            showDeleteModal(null);
            setSelectedSession(defaultSession);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setPerformingAction(false);
        }
    };

    const init = async () => {
        try {
            setProcessing(true);
            const res = await getAllTechnicalSessionsDT();
            const institutions = await getAllInstitutions();
            setRecords(res?.resultObject?.data || []);
            setInstitutions(institutions?.resultObject || []);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const searchFunction = async (values, closeSearchModal) => {
        try {
            const res = await getAllTechnicalSessionsDT(values);
            setRecords(res?.resultObject?.data || []);
            setExistingSearch(values);
            closeSearchModal();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const exportData = async () => {
        try {
            setExporting(true);
            const resp = await getAllTechnicalSessionsDT(existingSearch, true);
            enqueueSnackbar(resp?.message || 'Your report is being processed, kindly check the reports page for progress');
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer
            pageTitle="All Technical Session"
            tagline="Manage all technical session"
            processing={processing}
            buttonComponent={
                <>
                    <Button
                        onClick={() => {
                            setSelectedSession(defaultSession);
                            setActionModal(true);
                        }}
                        startIcon={<BusinessOutlined />}
                        style={{ margin: '0 .5rem' }}
                        variant="contained"
                    >
                        Create Session
                    </Button>
                </>
            }
        >
            <Filters searchFields={[]} searchFunction={searchFunction} advancedSearchFields columns={technicalSessionColumns} showFilter={false} />

            <Box>
                <div className="paper" style={{ height: 550, width: '100%', textAlign: 'center' }}>
                    {records?.length > 0 && (
                        <div className="export-btn ">
                            <Button onClick={exportData} variant="contained" startIcon={<CloudDownloadOutlined />}>
                                {exporting ? <CircularProgress color="inherit" size={15} /> : 'Export'}
                            </Button>
                        </div>
                    )}
                    <DataGrid
                        className={'center-first-column'}
                        rows={records}
                        getRowId={(row) => row.id}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        checkboxSelection={false}
                        disableSelectionOnClick
                        style={{ fontSize: '12px' }}
                    />
                </div>
            </Box>

            <Dialog open={Boolean(actionModal)}>
                <h4 className="text-center mt-2">{Boolean(selectedSession?.id) ? 'Edit' : 'Create'} Technical Sessions</h4>
                <Formik
                    initialValues={{ ...selectedSession }}
                    enableReinitialize
                    onSubmit={performAction}
                    validationSchema={Yup.object().shape({
                        title: Yup.string().required('Title is required.'),
                        description: Yup.string().required('Description is required.'),
                        startDate: Yup.string().required('Start Date is required.'),
                        endDate: Yup.string().required('End Date is required.'),
                    })}
                >
                    {({ isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <DialogContent>
                                <Grid container spacing={3} paddingBottom={3}>
                                    <Grid item xs={12}>
                                        <TextInput id="title" required label="Title" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextInput id="description" multiline minRows={2} required label="Description" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <SelectInput id="institutionId" options={institutions} required field="institutionId" fieldDisplay="name" minRows={2} label="Institution" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextInput id="startDate" type="date" required label="Start Date" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextInput id="endDate" type="date" required label="End Date" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DocumentInput
                                            id="attachmentDocument"
                                            value={values.documentFileName}
                                            viewFunction={async () => {
                                                await getDocument(values?.documentId, values?.documentFileName);
                                            }}
                                            required
                                            label="Document"
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Button
                                            disabled={isSubmitting}
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => {
                                                if (!performingAction) {
                                                    setActionModal(false);
                                                    setSelectedSession(defaultSession);
                                                }
                                            }}
                                            style={{ marginTop: '.5rem' }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button disabled={isSubmitting} fullWidth variant="contained" type="submit" style={{ marginTop: '.5rem' }}>
                                            {isSubmitting ? <CircularProgress size={15} color="inherit" /> : values?.id ? 'Edit Session' : 'Add Session'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Dialog
                open={Boolean(deleteModal)}
                onClose={() => {
                    if (!performingAction) {
                        showDeleteModal(false);
                        setSelectedSession(defaultSession);
                    }
                }}
            >
                <h4 className="text-center mt-2 mb-0">Confirm Technical Session Deletion</h4>
                <Formik initialValues={{ ...selectedSession }} enableReinitialize onSubmit={deleteSessionData}>
                    {({ isSubmitting, values }) => (
                        <Form>
                            <DialogContent>
                                <Grid container spacing={3} paddingBottom={3}>
                                    <Grid item xs={12}>
                                        Kindly confirm you want to delete warning with details below:
                                        <br />
                                        <br />
                                        {/* <b>ID:</b> {values?.id} <br /> */}
                                        <b>Title:</b> {values?.title} <br />
                                        <b>Description:</b> {values?.description} <br />
                                        <b>Institution:</b> {values?.institutionName} <br />
                                        <b>Start Date:</b> {moment(values?.startDate).format('DD MMMM, YYYY - hh:mm A')} <br />
                                        <b>End Date:</b> {moment(values?.endDate).format('DD MMMM, YYYY  - hh:mm A')}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button
                                            disabled={isSubmitting}
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => {
                                                if (!performingAction) {
                                                    showDeleteModal(false);
                                                    setSelectedSession(defaultSession);
                                                }
                                            }}
                                            style={{ marginTop: '.5rem' }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button disabled={isSubmitting} startIcon={<DeleteOutlined />} color="error" fullWidth variant="contained" type="submit" style={{ marginTop: '.5rem' }}>
                                            {isSubmitting ? <CircularProgress size={15} color="inherit" /> : 'Delete Session'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </PageContainer>
    );
};

export default TechnicalSessions;
